// ---------------------------------------------------------------------------
// Display size (in-app zoom) — like the browser's 80% / 90% / 100%, but for
// this app only, on every page, on PC, laptop and Android.
//
// The whole app is scaled with CSS `zoom` on <html>. The choice is saved per
// device (localStorage), so a small laptop can use 80% while a big office
// screen stays at 100%. "Auto" picks a size from the screen width.
//
// To change the choices: edit SCALES below (and AUTO_STEPS for Auto).
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'app_display_scale';

// Values offered in the footer list. 'auto' + percentages.
export const SCALES = ['auto', 70, 75, 80, 90, 100, 110, 125];
export const DEFAULT_SCALE = 100; // what a new device starts with

// Auto: screen width (CSS px, at 100%) -> size. First match wins.
// Phones/tablets (< 1024px) stay at 100%: their layout is already made for
// small screens, and shrinking it would only make text harder to tap/read.
const AUTO_STEPS = [
  { minWidth: 1700, scale: 100 },
  { minWidth: 1440, scale: 90 },
  { minWidth: 1024, scale: 80 },
  { minWidth: 0, scale: 100 },
];

export function getScaleSetting() {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'auto') return 'auto';
    const n = Number(saved);
    return SCALES.includes(n) ? n : DEFAULT_SCALE;
  } catch (e) {
    return DEFAULT_SCALE;
  }
}

// The real percentage for a setting ('auto' is resolved from the width).
export function resolveScale(setting) {
  if (setting !== 'auto') return setting;
  const w = window.innerWidth;
  return AUTO_STEPS.find((s) => w >= s.minWidth).scale;
}

let autoListener = null;

export function applyScale(setting) {
  const pct = resolveScale(setting);
  const html = document.documentElement;
  html.style.zoom = pct === 100 ? '' : String(pct / 100);
  html.setAttribute('data-scale', String(pct));

  // In Auto, follow window resizes / phone rotation.
  if (setting === 'auto' && !autoListener) {
    autoListener = () => applyScale('auto');
    window.addEventListener('resize', autoListener);
  } else if (setting !== 'auto' && autoListener) {
    window.removeEventListener('resize', autoListener);
    autoListener = null;
  }
  return pct;
}

export function setScale(setting) {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(setting));
  } catch (e) {
    /* storage blocked: the size still changes for this visit */
  }
  return applyScale(setting);
}
